import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../api/client";
import AlertModal from "../components/AlertModal";
import { useData } from "../data/DataProvider";

export default function PaymentWebViewScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAllData } = useData();

  // CheckoutScreen veya konum durumundan rawItem verisini alıyoruz.
  const { posData: routePosData, rawItem: directRawItem } = location.state || {};
  const rawItem =
    routePosData?.rawItem ||
    directRawItem ||
    (location.state?.installment_id ? location.state : null);

  const [isLoading, setIsLoading] = useState(true);
  const [htmlContent, setHtmlContent] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "success",
  });
  const [errorMessage, setErrorMessage] = useState(null);

  const handleCloseModal = async () => {
    setModalVisible(false);
    if (modalConfig.type === "success") {
      navigate("/installment", { state: { forceRefresh: true } });
    } else {
      navigate("/installment");
    }
  };

  useEffect(() => {
    const initializePayment = async () => {
      try {
        // Backend'e ödeme başlatma isteği atıyoruz.
        const amount =
          rawItem?.balance ??
          (Number(rawItem?.amount || 0) - Number(rawItem?.paid_amount || 0));

        const response = await apiClient.post("/payments/init-pos", {
          installment_id: rawItem?.installment_id,
          installment_number: rawItem?.installment_number,
          amount: amount,
        });

        const returnedHtml = response.data?.html_content;

        if (returnedHtml) {
          const wrappedHtml = `
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="utf-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <style>
                  body { display: flex; justify-content: center; align-items: center; min-height: 100vh; background-color: #f3f4f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; }
                  .loader { border: 4px solid #e5e7eb; border-top: 4px solid #1e3a8a; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto; }
                  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                </style>
              </head>
              <body>
                <div style="text-align: center;">
                  <div class="loader"></div>
                  <p style="margin-top: 16px; color: #4b5563; font-size: 15px;">Bankanın ödeme sayfasına yönlendiriliyorsunuz...</p>
                </div>
                ${returnedHtml}
                <script>
                  if (!window.ReactNativeWebView) {
                    window.ReactNativeWebView = {
                      postMessage: function(data) {
                        window.parent.postMessage(data, '*');
                      }
                    };
                  }
                  setTimeout(function() {
                    var content = document.body ? document.body.innerText : '';
                    if (content.trim().startsWith('{') && content.trim().endsWith('}')) {
                      window.ReactNativeWebView.postMessage(content);
                    }
                  }, 500);
                </script>
              </body>
            </html>
          `;
          setHtmlContent(wrappedHtml);
        } else {
          setModalConfig({
            title: "Hata",
            message: "Ödeme sayfası oluşturulamadı (HTML içeriği boş).",
            type: "error",
          });
          setModalVisible(true);
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Ödeme işlemi başlatılırken bir hata oluştu.';
        console.error("Ödeme başlatılamadı:", error);
        setModalConfig({
          title: "Hata",
          message: errorMessage,
          type: "error",
        });
        setModalVisible(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (rawItem) {
      initializePayment();
    } else {
      setErrorMessage("Ödeme bilgisi (taksit) bulunamadı.");
      setIsLoading(false);
    }
  }, [rawItem]);

  // iframe veya banka iframe'inden gelen postMessage dinleyicisi
  useEffect(() => {
    const handleMessage = (event) => {
      try {
        let data = event.data;
        if (typeof data === "string" && data.trim().startsWith("{")) {
          data = JSON.parse(data);
        }
        console.log("Backend JSON Cevabı:", data);

        if (data && typeof data === "object") {
          if (!data.success && (data.message || data.error)) {
            setModalConfig({
              title: "Ödeme Sonucu",
              message:
                data.message || data.error || "Ödeme işlemi başarısız oldu.",
              type: "error",
            });
            setModalVisible(true);
          } else if (data.success) {
            setModalConfig({
              title: "Başarılı",
              message: "Ödemeniz başarıyla tamamlandı.",
              type: "success",
            });
            setModalVisible(true);
          }
        }
      } catch (e) {
        console.log("Message parsing error:", e);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-[70vh] bg-white flex flex-col justify-center items-center p-6">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-[#1e3a8a] rounded-full animate-spin mb-4" />
        <p className="text-gray-600 text-base font-medium">
          Ödeme İşlemi Başlatılıyor...
        </p>
      </div>
    );
  }

  if (errorMessage || !htmlContent) {
    return (
      <div className="min-h-[70vh] bg-white flex flex-col justify-center items-center p-6 text-center">
        <p className="text-gray-600 text-base mb-4">
          {errorMessage || "Ödeme sayfası yüklenemedi."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/installment")}
          className="px-6 py-2.5 bg-[#1e3a8a] hover:bg-blue-900 text-white rounded-xl font-semibold transition-colors cursor-pointer"
        >
          Taksitler Sayfasına Dön
        </button>

        <AlertModal
          visible={modalVisible}
          onClose={handleCloseModal}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
        />
      </div>
    );
  }

  return (
    <div className="min-h-[85vh] bg-[#f8f9ff] p-4 md:p-6 flex flex-col items-center justify-center">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden min-h-[650px] flex flex-col">
        <iframe
          title="Ödeme Sayfası"
          srcDoc={htmlContent}
          className="w-full flex-1 min-h-[650px] border-0"
          sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation"
        />
      </div>

      <AlertModal
        visible={modalVisible}
        onClose={handleCloseModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
    </div>
  );
}

